import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useState, useEffect } from "react";

import API_Calls_Dashboard from "@/api/calls/political/API_Calls_Dashboard";
import Notifications from "@/components/Notifications/Notifications";
import { getComparator } from "@/components/political/Dashboard/dashboardUtils";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { canViewAllAffiliates } from "@/utils/permissionsHandler";
import { formatTimestamp } from "@/utils/util";

const headCells = [
  {
    id: "displayFilename",
    numeric: false,
    disablePadding: false,
    label: "File Name",
    sortable: true,
  },
  {
    id: "processed",
    numeric: false,
    disablePadding: false,
    label: "Processed Date",
    sortable: true,
  },

  {
    id: "local",
    numeric: false,
    disablePadding: false,
    label: "Local",
    sortable: true,
  },
  // {
  //   id: "recordCount",
  //   numeric: true,
  //   disablePadding: false,
  //   label: "Record Count",
  //   sortable: true,
  // },
  {
    id: "source",
    numeric: false,
    disablePadding: false,
    label: "Source",
    sortable: true,
  },

  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Actions",
    sortable: false,
  },
];

function TableHeader({ order, orderBy, onRequestSort }) {
  const { user } = useAuth0();
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells
          .filter(
            (cell) => !(cell.label === "Local" && !canViewAllAffiliates(user))
          )
          .map((headCell) => (
            <TableCell
              key={headCell.id}
              align="left"
              sortDirection={
                headCell.sortable && orderBy === headCell.id ? order : false
              }
            >
              {headCell.sortable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "desc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}

export default function VolunteerDataTable({ files }) {
  const [rows, setRows] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("processed");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [visibleRows, setVisibleRows] = useState([]);
  const [notification, setNotification] = useState({});
  const { user } = useAuth0();
  const { getVolunteerDataFile } = API_Calls_Dashboard();

  useEffect(() => {
    createRows(files);
    // createRows is an inline function recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const sourceMap = {
    member: "Member Mobilization",
    political: "Electoral & Legislative",
    solidarity: "Solidarity Now",
  };

  const createRows = (files) => {
    // ['member', 'LOCAL_1000', 'processed', '2026-04-28T09:59:29', '25 rows good local key.csv@2026-04-28T09:58:55', 'woo@2026-04-28T09:59:29', 'ada.parquet']
    const rows = [];
    files.forEach((file) => {
      const parts = file.ada_file.split("/");
      const row = {
        localId: parts[1],
        processed: parts[3],
        filename: parts[4],
        displayFilename: parts[4].split("@")[0],
        localName: file.local_name,
        recordCount: file.num_rows,
        displaySource: sourceMap[parts[0]],
        source: parts[0],
        deFile: file.de_file,
      };
      rows.push(row);
    });
    setRows(rows);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  useEffect(() => {
    const sortedRows = [...rows].sort(getComparator(order, orderBy));
    const slicedRows = sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    setVisibleRows(slicedRows);
  }, [rows, order, orderBy, page, rowsPerPage]);

  const handleFileDownload = async (row) => {
    const [name, created] = row.filename.split("@");
    // member/SEIU_ADA/raw_data/2026-04-30T09:40:00/volunteer-data.csv
    const filename = `${row.source}/${row.localId}/raw_data/${created}/${name}`;
    const res = await getVolunteerDataFile(
      row.localId,
      filename,
      created,
      row.source
    );
    if (res.error) {
      setNotification({
        message: "There was an error fetching this download.",
        severity: "error",
        messageDuration: 7000,
      });
    }
  };

  const handleDEFileDownload = async (row) => {
    const [shortName, created] = row.filename.split("@");
    const filename = row.deFile;
    const res = await getVolunteerDataFile(
      row.localId,
      filename,
      created,
      row.source,
      shortName
    );
    if (res.error) {
      setNotification({
        message: "There was an error fetching this download.",
        severity: "error",
        messageDuration: 7000,
      });
    }
  };

  return (
    <>
      <Box>
        <Paper>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="VolunteerDataTable"
              data-testid="volunteer-data-table"
            >
              <TableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow tabIndex={-1} key={row.processed}>
                      <TableCell align="left">{row.displayFilename}</TableCell>
                      <TableCell component="th" id={labelId} scope="row">
                        {formatTimestamp(row.processed)}
                      </TableCell>

                      {canViewAllAffiliates(user) && (
                        <TableCell align="left">{row.localName}</TableCell>
                      )}
                      {/* <TableCell align="center">
                        {formatLargeNumber(row.recordCount)}
                      </TableCell> */}
                      <TableCell align="left">{row.displaySource}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex" }}>
                          <Box sx={{ marginRight: "8px" }}>
                            <Tooltip title="Download volunteer data file">
                              <IconButton
                                onClick={() => handleFileDownload(row)}
                              >
                                <SiteIcons iconstring="download" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box>
                            {row.deFile && (
                              <Tooltip title="Download enhanced data file">
                                <IconButton
                                  onClick={() => handleDEFileDownload(row)}
                                >
                                  <SiteIcons iconstring="description" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}
